import {
  sendAnalyticsError,
  sendAnalyticsInfo,
} from 'features/analytics/helpers';
import { CallDaoService } from 'features/call/call-base/services';
import {
  CaptionStatusType,
  RequesterType,
} from 'features/call/call-captions/enums';

interface OpenCaptionStatusReport {
  callId: number;
  requesterType: RequesterType;
  userId: number;
}

// Untyped thunks expose ThunkDispatch<unknown, ...>, which is not AppDispatch.
// Only the analytics actions are dispatched from here.
type CaptionStatusDispatch = (
  action: ReturnType<typeof sendAnalyticsInfo>
) => unknown;

export interface ReportCaptionStatusOptions {
  callId: number;
  captionStatusType: CaptionStatusType;
  requesterType: RequesterType;
  userId: number | null;
  keepAlive?: boolean;
  dispatch?: CaptionStatusDispatch;
}

// Lives outside the captions slice so resetCallCaptions cannot drop an On
// before its Off is sent. Keyed by requester because only one open On per
// requester is reported.
const openReports = new Map<RequesterType, OpenCaptionStatusReport>();

let queue: Promise<void> = Promise.resolve();

const enqueue = (task: () => Promise<void>) => {
  const run = queue.then(task, task);
  queue = run.then(
    () => undefined,
    () => undefined
  );
  return run;
};

// Bounds a caption-status PUT so a request that never settles cannot hold
// call close, unload, or an ownership handoff open.
export const CAPTION_STATUS_REQUEST_TIMEOUT_MS = 5_000;

const recordCaptionStatus = (
  report: OpenCaptionStatusReport,
  captionStatusType: CaptionStatusType,
  keepAlive?: boolean
) =>
  CallDaoService.callCaptionStatus({
    callId: report.callId,
    captionStatusType,
    requesterType: report.requesterType,
    userId: report.userId,
    keepAlive,
  });

const withTimeout = (request: Promise<void>, timeoutMs: number) =>
  new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error('Caption status request timed out'));
    }, timeoutMs);

    request.then(
      () => {
        clearTimeout(timer);
        resolve();
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });

const sendCaptionStatus = async (
  report: OpenCaptionStatusReport,
  captionStatusType: CaptionStatusType,
  keepAlive?: boolean,
  dispatch?: CaptionStatusDispatch
) => {
  const request = recordCaptionStatus(report, captionStatusType, keepAlive);
  void request.catch(() => undefined);

  try {
    await withTimeout(request, CAPTION_STATUS_REQUEST_TIMEOUT_MS);
    dispatch?.(
      sendAnalyticsInfo({
        Method: 'recordCaptionStatus',
        Message: 'Caption status reported',
        callId: report.callId,
        captionStatusType,
        requesterType: report.requesterType,
        userId: report.userId,
      })
    );
  } catch (error) {
    dispatch?.(
      sendAnalyticsError({
        Method: 'recordCaptionStatus',
        Message: 'Caption status report failed',
        error,
        callId: report.callId,
        captionStatusType,
        requesterType: report.requesterType,
        userId: report.userId,
      })
    );
  }
};

const canReportOn = ({
  callId,
  requesterType,
  userId,
}: ReportCaptionStatusOptions) => {
  if (!callId || userId == null || openReports.has(requesterType)) {
    return false;
  }

  // 0 is reserved for the endpoint. A missing interpreter id must not be sent.
  return userId !== 0 || requesterType === RequesterType.Endpoint;
};

const reportOn = (options: ReportCaptionStatusOptions) => {
  if (!canReportOn(options)) {
    return Promise.resolve();
  }

  const { callId, requesterType, userId, keepAlive, dispatch } = options;

  if (userId == null) {
    return Promise.resolve();
  }

  const report = { callId, requesterType, userId };
  openReports.set(requesterType, report);

  return enqueue(() =>
    sendCaptionStatus(report, CaptionStatusType.On, keepAlive, dispatch)
  );
};

const reportOff = ({
  requesterType,
  keepAlive,
  dispatch,
}: ReportCaptionStatusOptions) => {
  const report = openReports.get(requesterType);

  if (!report) {
    return Promise.resolve();
  }

  return enqueue(async () => {
    await sendCaptionStatus(report, CaptionStatusType.Off, keepAlive, dispatch);
    // A failed or timed-out Off is counted as reported. Close must not wait
    // for a retry, and a later flush must not send it again.
    openReports.delete(requesterType);
  });
};

export const reportCaptionStatus = (options: ReportCaptionStatusOptions) =>
  options.captionStatusType === CaptionStatusType.On
    ? reportOn(options)
    : reportOff(options);

export const flushOpenCaptionStatuses = async ({
  keepAlive,
  dispatch,
}: {
  keepAlive?: boolean;
  dispatch?: CaptionStatusDispatch;
} = {}) => {
  try {
    await enqueue(async () => {
      for (const report of [...openReports.values()]) {
        await sendCaptionStatus(
          report,
          CaptionStatusType.Off,
          keepAlive,
          dispatch
        );
        openReports.delete(report.requesterType);
      }
    });
  } catch (error) {
    dispatch?.(
      sendAnalyticsError({
        Method: 'flushOpenCaptionStatuses',
        Message: 'Caption status flush failed',
        error,
      })
    );
    openReports.clear();
  }
};

export const resetCaptionStatusReporter = () => {
  openReports.clear();
  queue = Promise.resolve();
};
